/**
 * The pool. A hundred accounts only stay manageable if the ones that are broken look broken, so a
 * stale box, an unfilled box and an expired token are all surfaced on the collapsed row rather than
 * hidden one tap deep. One account expands at a time; a hundred inline editors in the DOM is a
 * phone-hostile way to render a list.
 */
import { useEffect, useState } from "react";
import { api, errorMessage } from "../api.ts";
import { duration, euros, isoDay, LIFECYCLE_COPY, maskedCard, relativeTime, ROLE_COPY } from "../format.ts";
import { useApi } from "../hooks.ts";
import { useModel } from "../model.tsx";
import { Icon } from "../components/icons.tsx";
import { CardFace } from "../components/CardFace.tsx";
import { Badge, Empty, Note, Slots, Spinner, Stat } from "../components/ui.tsx";
import type { AccountLifecycle, AccountRole, AccountSummary } from "../../../server/types.ts";

const LIFECYCLES: AccountLifecycle[] = ["FRESH", "SEEDING", "WARM", "STALE", "RETIRED"];
const ROLES: AccountRole[] = ["PRIMARY", "BACKUP", "EXCLUDED"];

/** Everything that makes an account unusable tonight, in the words the operator needs. */
function problems(account: AccountSummary, weekStart: string | null, now: number): string[] {
	const issues: string[] = [];
	if (account.bonusCard === null) issues.push("no Bonuskaart, so it can never be used");
	if (account.boxWeekStart === null) issues.push("box never fetched");
	else if (weekStart !== null && account.boxWeekStart !== weekStart) issues.push(`box is from ${isoDay(account.boxWeekStart)}`);
	if (account.boxError === "BONUS_BOX_NOT_FILLED") issues.push("AH has not filled a box for it yet");
	else if (account.boxError !== null) issues.push(account.boxError);
	else if (account.boxMaxActivations === 0 && account.boxWeekStart !== null) issues.push("no activation slots");
	if (account.tokenExpiresAt !== null && account.tokenExpiresAt < now) issues.push("access token expired");
	return issues;
}

export function Accounts() {
	const model = useModel();
	const loaded = useApi((signal) => api.accounts({ signal }), []);
	const [rows, setRows] = useState<AccountSummary[] | null>(null);
	const [open, setOpen] = useState<number | null>(null);
	const [busy, setBusy] = useState<number | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [query, setQuery] = useState("");
	const [onlyProblems, setOnlyProblems] = useState(false);
	const [newLink, setNewLink] = useState<{ categoryId: number; role: AccountRole } | null>(null);
	const [barcode, setBarcode] = useState<{ card: string; email: string } | null>(null);

	useEffect(() => {
		if (loaded.data !== null) setRows(loaded.data);
	}, [loaded.data]);

	const now = Date.now();
	const weekStart = model.boot?.week.startDate ?? null;

	const act = async (id: number, op: () => Promise<AccountSummary>): Promise<void> => {
		setBusy(id);
		setError(null);
		try {
			const next = await op();
			setRows((current) => current?.map((row) => (row.id === next.id ? next : row)) ?? null);
		} catch (err) {
			setError(errorMessage(err));
		} finally {
			setBusy(null);
		}
	};

	const needle = query.trim().toLowerCase();
	const visible = (rows ?? [])
		.filter((account) => {
			if (needle !== "" && !account.email.toLowerCase().includes(needle) && !(account.bonusCard ?? "").includes(needle)) {
				return false;
			}
			return !onlyProblems || problems(account, weekStart, now).length > 0;
		})
		.sort((a, b) => {
			const pa = problems(a, weekStart, now).length > 0 ? 0 : 1;
			const pb = problems(b, weekStart, now).length > 0 ? 0 : 1;
			if (pa !== pb) return pa - pb;
			if (a.tripCount !== b.tripCount) return b.tripCount - a.tripCount;
			return a.email.localeCompare(b.email);
		});

	const broken = (rows ?? []).filter((account) => problems(account, weekStart, now).length > 0).length;

	if (barcode !== null) {
		return <CardFace card={barcode.card} label={barcode.email} onClose={() => setBarcode(null)} />;
	}

	return (
		<>
			<main className="screen stack">
				<section className="panel">
					<h1 className="panel-title">Pool</h1>
					<div className="stats">
						<Stat label="Accounts" value={String(rows?.length ?? 0)} />
						<Stat label="Need attention" value={String(broken)} />
						<Stat label="Warm" value={String((rows ?? []).filter((a) => a.lifecycle === "WARM").length)} />
						<Stat label="Bonus week" value={weekStart === null ? "\u2014" : isoDay(weekStart)} />
					</div>
					<div className="filters">
						<input
							value={query}
							onChange={(event) => setQuery(event.target.value)}
							placeholder="Filter by email or card"
							aria-label="Filter accounts"
							autoComplete="off"
						/>
						<button
							type="button"
							className={onlyProblems ? "chip chip-on" : "chip"}
							aria-pressed={onlyProblems}
							onClick={() => setOnlyProblems(!onlyProblems)}
						>
							<Icon name="alert" /> Problems only
						</button>
						<button type="button" className="chip" onClick={loaded.reload}>
							<Icon name="refresh" /> Reload
						</button>
					</div>
				</section>

				{error !== null && <Note tone="danger">{error}</Note>}
				{loaded.error !== null && <Note tone="danger">{loaded.error}</Note>}
				{rows === null && loaded.loading && (
					<div className="loading">
						<Spinner label="Reading the pool" />
					</div>
				)}
				{rows !== null && visible.length === 0 && (
					<Empty title="Nothing matches">
						{onlyProblems ? "Every account has a fresh box and a live token." : "No account matches that filter."}
					</Empty>
				)}

				<ul className="accounts">
					{visible.map((account) => {
						const issues = problems(account, weekStart, now);
						const expanded = open === account.id;
						const stale = weekStart !== null && account.boxWeekStart !== weekStart;
						const unlinked = model.categories.filter(
							(category) => !account.links.some((link) => link.categoryId === category.id),
						);
						return (
							<li key={account.id} className={issues.length > 0 ? "acct acct-broken" : "acct"}>
								<button
									type="button"
									className="acct-head"
									aria-expanded={expanded}
									onClick={() => {
										setOpen(expanded ? null : account.id);
										setNewLink(null);
									}}
								>
									<span className="acct-card">{maskedCard(account.bonusCard)}</span>
									<span className="acct-email">{account.email}</span>
									<span className="acct-badges">
										<Badge
											tone={
												account.lifecycle === "WARM"
													? "money"
													: account.lifecycle === "SEEDING"
														? "accent"
														: account.lifecycle === "RETIRED"
															? "danger"
															: "neutral"
											}
										>
											{LIFECYCLE_COPY[account.lifecycle].label}
										</Badge>
										{account.links.map((link) => (
											<Badge key={link.categoryId} tone={link.role === "EXCLUDED" ? "danger" : "neutral"}>
												{model.categories.find((category) => category.id === link.categoryId)?.name ??
													`#${link.categoryId}`}
												{link.pinned ? " \u00b7 pinned" : ""}
											</Badge>
										))}
									</span>
									<span className={stale ? "acct-box acct-box-stale" : "acct-box"}>
										<Slots used={account.boxActive} free={Math.max(0, account.boxMaxActivations - account.boxActive)} />
										<span>
											{account.boxOffers} offer{account.boxOffers === 1 ? "" : "s"}
											{account.boxFetchedAt === null ? "" : ` \u00b7 read ${relativeTime(account.boxFetchedAt, now)}`}
										</span>
									</span>
									<Icon name="chevron" />
								</button>

								{issues.length > 0 && (
									<ul className="acct-issues">
										{issues.map((issue) => (
											<li key={issue}>
												<Icon name="alert" /> {issue}
											</li>
										))}
									</ul>
								)}

								{expanded && (
									<div className="acct-body">
										<div className="stats">
											<Stat label="Trips" value={String(account.tripCount)} />
											<Stat label="Profile mass" value={euros(account.profileMassCents)} hint="decayed spend" />
											<Stat
												label="Last used"
												value={account.lastUsedAt === null ? "never" : relativeTime(account.lastUsedAt, now)}
											/>
											<Stat
												label="Box week"
												value={account.boxWeekStart === null ? "never" : isoDay(account.boxWeekStart)}
											/>
											<Stat label="Slots (K)" value={`${account.boxActive} / ${account.boxMaxActivations}`} />
											<Stat
												label="Token"
												value={
													account.tokenExpiresAt === null
														? "unknown"
														: account.tokenExpiresAt < now
															? "expired"
															: `${duration(account.tokenExpiresAt - now)} left`
												}
											/>
										</div>

										<div className="acct-row">
											<button
												type="button"
												className="btn btn-primary"
												disabled={account.bonusCard === null}
												onClick={() => {
													if (account.bonusCard !== null) {
														setBarcode({ card: account.bonusCard, email: account.email });
													}
												}}
											>
												<Icon name="card" /> Show barcode
											</button>
											<span className="acct-hint">
												Manual use only: does not activate or verify offers, or record this basket as a trip.
											</span>
										</div>

										<div className="acct-row">
											<label htmlFor={`life-${account.id}`}>Lifecycle</label>
											<select
												id={`life-${account.id}`}
												value={account.lifecycle}
												disabled={busy === account.id}
												onChange={(event) => {
													const next = LIFECYCLES.find((state) => state === event.target.value);
													if (next !== undefined) void act(account.id, () => api.setLifecycle(account.id, next));
												}}
											>
												{LIFECYCLES.map((state) => (
													<option key={state} value={state}>
														{LIFECYCLE_COPY[state].label}
													</option>
												))}
											</select>
											<span className="acct-hint">{LIFECYCLE_COPY[account.lifecycle].detail}</span>
										</div>

										<h3 className="panel-sub">Category links</h3>
										{account.links.length === 0 && (
											<p className="acct-hint">
												No links, so this card is a generalist: eligible for any trip at tier 2 and free of a
												profile to protect.
											</p>
										)}
										<ul className="links">
											{account.links.map((link) => (
												<li key={link.categoryId} className="link">
													<span className="link-name">
														{model.categories.find((category) => category.id === link.categoryId)?.name ??
															`#${link.categoryId}`}
													</span>
													<span className="segmented">
														{ROLES.map((role) => (
															<button
																key={role}
																type="button"
																className={link.role === role ? "seg seg-on" : "seg"}
																disabled={busy === account.id}
																onClick={() =>
																	void act(account.id, () =>
																		api.setLink(account.id, link.categoryId, role, link.pinned),
																	)
																}
															>
																{ROLE_COPY[role]}
															</button>
														))}
													</span>
													<button
														type="button"
														className={link.pinned ? "chip chip-on" : "chip"}
														aria-pressed={link.pinned}
														title="Pinned links survive nightly relearning"
														disabled={busy === account.id}
														onClick={() =>
															void act(account.id, () =>
																api.setLink(account.id, link.categoryId, link.role, !link.pinned),
															)
														}
													>
														<Icon name="pin" /> {link.pinned ? "Pinned" : "Learned"}
													</button>
													<button
														type="button"
														className="chip chip-danger"
														aria-label={`Remove ${model.boot?.categories.find((category) => category.id === link.categoryId)?.name ?? "category"} link`}
														title={`Remove ${model.boot?.categories.find((category) => category.id === link.categoryId)?.name ?? "category"} link`}
														disabled={busy === account.id}
														onClick={() =>
															void act(account.id, () => api.setLink(account.id, link.categoryId, null, false))
														}
													>
														<Icon name="close" />
													</button>
												</li>
											))}
										</ul>

										{unlinked.length > 0 && (
											<div className="acct-row">
												<select
													aria-label="Category to link"
													value={newLink?.categoryId ?? ""}
													onChange={(event) => {
														const id = Number(event.target.value);
														setNewLink(Number.isSafeInteger(id) && id > 0 ? { categoryId: id, role: "PRIMARY" } : null);
													}}
												>
													<option value="">Link a category&hellip;</option>
													{unlinked.map((category) => (
														<option key={category.id} value={category.id}>
															{category.name}
														</option>
													))}
												</select>
												{newLink !== null && (
													<span className="segmented">
														{ROLES.map((role) => (
															<button
																key={role}
																type="button"
																className={newLink.role === role ? "seg seg-on" : "seg"}
																onClick={() => setNewLink({ categoryId: newLink.categoryId, role })}
															>
																{ROLE_COPY[role]}
															</button>
														))}
													</span>
												)}
												<button
													type="button"
													className="btn btn-quiet"
													disabled={newLink === null || busy === account.id}
													onClick={() => {
														if (newLink === null) return;
														const { categoryId, role } = newLink;
														setNewLink(null);
														void act(account.id, () => api.setLink(account.id, categoryId, role, true));
													}}
												>
													Pin link
												</button>
											</div>
										)}

										<div className="acct-row">
											<button
												type="button"
												className="btn btn-ghost"
												disabled={busy === account.id}
												onClick={() => void act(account.id, () => api.refreshAccount(account.id))}
											>
												{busy === account.id ? <Spinner label="Refreshing" /> : <Icon name="refresh" />}
												Refresh token and box
											</button>
											{account.boxError !== null && <code className="acct-code">{account.boxError}</code>}
										</div>
									</div>
								)}
							</li>
						);
					})}
				</ul>
			</main>
		</>
	);
}
